// material-ui
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

import { LocalizationProvider } from "@mui/x-date-pickers";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
// project imports
import CircularWithPath from "components/@extended/progress/CircularWithPath";
import { openSnackbar } from "api/snackbar";
// assets
// types
import { SnackbarProps } from "types/snackbar";
import { useAppDispatch } from "hooks";

import { Bank } from "model/basicInfo";

import { addBank, editBank } from "store/actions/main/basicInfo/bank";
import { FormattedMessage, useIntl } from "react-intl";

// constant
const getInitialValues = (customer: Bank | null) => {
  if (customer) {
    return {
      name: customer.name || "",
      branch: customer.branch || "",
      branch_trans: customer.branch_trans.ar,
      name_trans: customer.name_trans.ar,
      status: customer.status || false,
    };
  } else {
    return {
      name: "",
      branch: "",
      branch_trans: "",
      name_trans: "",
      status: false,
    };
  }
};

// ==============================|| BANK ADD / EDIT - FORM ||============================== //

const FormBankAdd = ({
  customer,
  closeModal,
  fetchData,
  loading,
}: {
  loading: any;
  setLoading: any;
  fetchData: any;
  id: number;
  customer: Bank | null;
  closeModal: () => void;
}) => {
  const dispatch = useAppDispatch();

  const CustomerSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    branch: Yup.string().required("Branch is required"),
  });

  const formik = useFormik({
    initialValues: getInitialValues(customer!),
    validationSchema: CustomerSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (customer) {
          // Dispatch editCategories action if customer exists
          await dispatch(
            editBank({
              id: customer.id || 0,
              data: {
                name: values.name,
                name_trans: {
                  ar: values.name_trans,
                },
                status: values.status,
                branch: values.branch,
                branch_trans: {
                  ar: values.branch_trans,
                },
              },
            })
          ).then((action) => {
            fetchData();
            if (action.type === "basicInfo/editBank/fulfilled") {
              openSnackbar({
                open: true,
                message: "Bank edited successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: {
                  color: "success",
                },
              } as SnackbarProps);
              setSubmitting(false);
              closeModal();
            }
          });
        } else {
          // Dispatch create newCat action if no customer exists
          await dispatch(
            addBank({
              name: values.name,
              name_trans: {
                ar: values.name_trans,
              },
              status: values.status,
              branch: values.branch,
              branch_trans: {
                ar: values.branch_trans,
              },
            })
          ).then((action) => {
            fetchData();
            if (action.type === "basicInfo/addBank/fulfilled") {
              openSnackbar({
                open: true,
                message: "Bank added successfully",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "alert",
                alert: {
                  color: "success",
                },
              } as SnackbarProps);
              setSubmitting(false);
              closeModal();
            }
          });
        }
        closeModal();
      } catch (error) {
        console.error(error);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  if (loading)
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );

  return (
    <>
      <FormikProvider value={formik}>
        <LocalizationProvider>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle
              style={{
                textAlign: "center",
                fontSize: "22px",
                fontWeight: "bold",
                color: "rgb(22, 119, 255)",
                marginBottom: "10px",
              }}
            >
              {customer ? (
                <FormattedMessage id="Edit Bank Details" />
              ) : (
                <FormattedMessage id="Add New Bank" />
              )}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={5} mb={1}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-name">
                      <FormattedMessage id="English Name" />{" "}
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="customer-name"
                      placeholder=<FormattedMessage id="Enter english name" />
                      {...getFieldProps("name")}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Stack>
                </Grid>
                <Grid item md={1} />
                <Grid item xs={12} md={5} mb={1}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-name">
                      <FormattedMessage id="Arabic Name" />{" "}
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="customer-name"
                      placeholder=<FormattedMessage id="Enter arabic name" />
                      {...getFieldProps("name_trans")}
                    />
                  </Stack>
                </Grid>
                <Grid item md={1} />
                <Grid item xs={12} sm={5}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-order">
                      <FormattedMessage id="Branch English Name" />
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="customer-order"
                      placeholder=<FormattedMessage id="Enter branch english name" />
                      {...getFieldProps("branch")}
                      error={Boolean(touched.branch && errors.branch)}
                      helperText={touched.branch && errors.branch}
                    />
                  </Stack>
                </Grid>
                <Grid item md={1} />
                <Grid item xs={12} sm={5}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-order">
                      <FormattedMessage id="Branch Arabic Name" />
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="customer-order"
                      placeholder=<FormattedMessage id="Enter branch arabic name" />
                      {...getFieldProps("branch_trans")}
                    />
                  </Stack>
                </Grid>
              </Grid>
              <Grid item xs={12} md={12} sx={{ marginTop: ".8rem" }}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="customer-is_active">
                    <FormattedMessage id="Status" />
                  </InputLabel>
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Switch
                          id="customer-is_active"
                          checked={formik.values.status}
                          {...getFieldProps("status")}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setFieldValue("status", checked);
                          }}
                        />
                      }
                      label={
                        formik.values.is_active ? (
                          <FormattedMessage id="Active" />
                        ) : (
                          <FormattedMessage id="Inactive" />
                        )
                      }
                    />
                  </FormControl>
                  {touched.is_active && errors.is_active && (
                    <FormHelperText
                      error
                      id="standard-weight-helper-text-email-login"
                      sx={{ pl: 1.75 }}
                    >
                      {errors.is_active}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    {customer ? (
                      <Button fullWidth type="submit" variant="contained">
                        {isSubmitting ? (
                          <CircularProgress
                            size="1.5rem"
                            style={{ color: "white" }}
                          />
                        ) : (
                          <FormattedMessage id="update" />
                        )}
                      </Button>
                    ) : (
                      <Button fullWidth type="submit" variant="contained">
                        {isSubmitting ? (
                          <CircularProgress
                            size="1.5rem"
                            style={{ color: "white" }}
                          />
                        ) : (
                          <FormattedMessage id="Submit" />
                        )}
                      </Button>
                    )}
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={closeModal}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
    </>
  );
};

export default FormBankAdd;
